export interface ISnack {
  open: boolean;
  message: string;
}
export const DEFAULT_SNACK: ISnack = {
  open: false, message: ""
}
export interface IPaginateRequest {
  limit: number;
  skip: number;
}
export interface ISearchRequest {
  search?: string;
}
export interface AwaitResponse {
  success?: boolean;
}
export interface ApiResponse<T = undefined> extends AwaitResponse {
  data?: T;
  err?: Error;
}
export interface IFileUploadResponse {
  url: string;
  error: string;
}

export interface IFormDataFileValue {name?: string | undefined; type?: string | undefined; uri: string}
export type UnwrapPromise<T> = T extends Promise<infer U> ? U : T;
export type BooleanLiteral = "true"|"false"


export type IInterviewType = "free" | "premium";
export interface IInterview {
  id: string;
  title: string;
  image: string;
  description: string;
  questions: IQuestion[];
  interviewType: IInterviewType;
}
export interface IUserInterview {
  id: string;
  interviewId: string;
  userId: string;
  completed: BooleanLiteral;
  score: number;
  remark: string;
  totalScore: number;
  // answers?: IUserQuestionAnswer[];
  createdAt: string;
}

type QuestionID = string;
type OptionID = string;
type QuestionAnswerID = string;
export interface IQuizUpdateData {
  quiz: {quizId: string; quiz: Partial<IQuiz>};
  questions: Record<QuestionID,Partial<IQuestion>>;
  questionOptions: Record<QuestionID, Record<OptionID, Partial<IQuestionOption>>>;
  questionAnswers: Record<QuestionID,Record<QuestionAnswerID, Partial<IQuestionAnswer>>>;
}
export interface IInterviewUpdateData extends Omit<IQuizUpdateData, "quiz"> {
  interview: {interviewId: string; interview: Partial<IInterview>};
}


export interface ICourseStat {
  total: number;
  premium: number;
  free: number;
  active: number;
  totalQuizzes: number;
}

type ILessonID = string;
type  IModuleID = string;
export type IModuleChanges = Record<IModuleID, Partial<IModule>>
export type ILessonChanges = Record<IModuleID, Record<ILessonID, Partial<ILesson>>>

export type ICourseType = "free" | "premium";
export type ICourseStudyType = "quiz" | "study"
export type ICourseStatus = "active" | "inactive"
export type ICourseCategory = "medicalExams" | "interview" | "license" | "others"
export const courseCategoryLabelMap = (key: ICourseCategory) => {
  switch(key){
    case "medicalExams": return "Behavioral Skills";
    case "interview": return "Interviews"
    case "license": return "Practice Focus"
    case "others": return "Others"
  }
}
export type IAdminRole = "rootAdmin"|"superAdmin"|"userAdmin"|"courseAdmin"
export interface IQuizYear {
  quizId: string;
  quizYear: string;
  totalQuestions: number;
}
export interface IQuizReport {
  score: number;
  remark: string;
  totalScore: number;
}
export interface ICourse {
  availableQuizzes: IQuizYear[];
  id: string;
  courseStudyType: ICourseStudyType;
  courseStatus: ICourseStatus;
  title: string;
  description: string;
  mode?: string;
  totalScore?: string;
  image: string;
  category: ICourseCategory;
  courseType: ICourseType;
  isHidden: BooleanLiteral;

  quizzes?: IQuiz[]

  courseModules: IModule[];
  learningObjectives: string;
  prerequisites: string;
  targetAudience: string;
  quizDescription?: string;
  quizPrepTips?: string;
}
export type IQuizMode = "timed" | "untimed"
export type IQuizState = "idle" | "loading" | "active" | "completed" | "paused";
export interface IQuiz {
  id: string;
  title?: string;
  description: string;
  courseId: string;
  year: string;
  questions?: IQuestion[];

  course?: ICourse;
}

export type IQuestionApiInput = Partial<Omit<IQuestion, "options"> & {
  options?: Partial<IQuestionOption>[]
}>
export interface IQuestion {
  id: string;
  quizId: string;
  interviewId: string;
  content: string;
  options?: IQuestionOption[];
  allowMultiple?: BooleanLiteral
}
export interface IQuestionOption {
  id: string;
  text: string;
  questionId: string;
}
export interface IQuestionAnswer {
  id: string;
  quizId?: string;
  interviewId?: string;
  questionId: string;
  optionIds: string[];
  explanation?: string;
}
export interface IUserQuestionAnswer {
  id: string;
  quizId: string;
  questionId: string;
  optionIds: string[];
}
export interface IUserQuiz {
  id: string;
  quizId: string;
  userId: string;
  completed: BooleanLiteral;
  score: number;
  remark: string;
  totalScore: number;
  answers?: IUserQuestionAnswer[];
  quizMode: IQuizMode;
  quiz?: IQuiz;
  createdAt: string;
}

export interface IModule {
  id: string;
  courseId: string;
  index: number;
  title: string;
  description: string;
  lessons?: ILesson[];
  activeKey: string[];
}

export interface ILesson {
  id: string;
  moduleId: string;
  index: number;
  title: string;
  content: string;
}
export interface IUserCourse {
  id: string;
  courseId: string;
  userId: string;
  score?: string;
  progress?: number;
  completed: BooleanLiteral;
  completedCourseModuleIds: string[];
  currentModuleId?: string;

  // lookup
  course?: ICourse;
  createdAt: string;
}

export interface ISiteContact {
    id: string;
    fullName: string;
    email: string;
    phoneNumber: string;
    reason: string;
    message: string;
  }
export type IUserStatus = "active"|"suspended"
export type IUserProfession = "clinician" | "student" | "others"


export interface IUserStatusCounts {
  total: number;
  active: number;
  suspended: number
}
export interface IUserAdmin {
  id: string;
  adminRole: IAdminRole;
  userId: string;
  createdAt: string;
}
export interface IUser {
  id: string;
  lastName: string;
  firstName: string;
  email: string;
  gender: string;
  profession: IUserProfession;
  country: string;
  studentLevel: string;
  testType: string; // usmle plab others
  lastOnline: string;
  avatar: string;
  onActivePremium: BooleanLiteral
  userStatus: IUserStatus;
  purpose: "examPrep" | "professionalDev" | "careerChange" | "personalInterest"
  userAdmin?: IUserAdmin
  createdAt: string;
}
export interface IUsersStat {
  totalUsers: number;
  premiumUsers: number;
  activeUsers: number;
  suspendedUsers: number;
  websiteVisits: number;
}
export interface INotification {}